import * as React from 'react'
import { useTranslation } from 'react-i18next'

import * as ToastPrimitives from '@radix-ui/react-toast'
import mergeRefs from 'merge-refs'
import { type VariantProps, tv } from 'tailwind-variants'
import { useResizeObserver } from 'usehooks-ts'

import CloseIcon from '@wwt/shared/assets/icons/outline/close.svg'

import { cn } from '@/shared/lib/cn'

export const ToastProvider = ToastPrimitives.Provider

export const ToastViewport = React.memo(
	({
		ref,
		className,
		...props
	}: React.ComponentPropsWithRef<typeof ToastPrimitives.Viewport>) => {
		const viewportRef = React.useRef<
			React.ElementRef<typeof ToastPrimitives.Viewport>
		>(null!)

		const { t } = useTranslation()

		useResizeObserver({
			ref: viewportRef,
			onResize: size => {
				document.documentElement.style.setProperty(
					'--radix-toast-viewport-height',
					`${size.height}px`
				)
			}
		})

		return (
			<ToastPrimitives.Viewport
				ref={mergeRefs(viewportRef, ref)}
				className={cn(
					// FIXME: z-index to 100 after removal of chakra-ui
					'fixed top-auto bottom-0 left-0 z-5500 flex max-h-screen w-full flex-col md:m-16 md:mb-[6.25rem] md:max-w-[400px] md:flex-col-reverse md:gap-3 2xl:mx-[4.25rem]',
					className
				)}
				label={t('toasts.label')}
				{...props}
			/>
		)
	}
)
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

// eslint-disable-next-line react-refresh/only-export-components
export const toastVariants = tv({
	base: 'group/toast pointer-events-auto max-md:pb-10 max-md:-mb-6 relative flex w-full items-center gap-x-4 rounded-md border p-4 pr-10 shadow-md transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=end]:translate-y-[var(--radix-toast-swipe-end-y)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:translate-y-[var(--radix-toast-swipe-move-y)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=open]:slide-in-from-bottom-full max-md:rounded-b-none max-md:border-b-0 max-md:data-[state=closed]:slide-out-to-bottom-full md:data-[state=closed]:slide-out-to-left-[468px] md:data-[state=open]:slide-in-from-top-full [&>svg]:shrink-0 [&>svg]:self-start',
	variants: {
		variant: {
			default: 'bg-background text-foreground',
			warning: 'bg-yellow-100 text-foreground',
			error: 'bg-red-100 text-foreground',
			success: 'bg-green-100 text-foreground',

			serverFailure:
				'bg-linear-to-r from-rose-400 to-orange-400 text-white border-none [&_svg]:text-white',
			clientFailure:
				'bg-linear-to-l from-indigo-400 to-purple-400 text-white border-none [&_svg]:text-white',
			serverUnavailable:
				'from-gray-900 to-gray-600 bg-linear-to-r text-white border-none [&_svg]:text-white'
		}
	},
	defaultVariants: {
		variant: 'default'
	}
})

export const Toast = ({
	ref,
	className,
	variant,
	...props
}: React.ComponentPropsWithRef<typeof ToastPrimitives.Root> &
	VariantProps<typeof toastVariants>) => {
	return (
		<ToastPrimitives.Root
			ref={ref}
			className={cn(toastVariants({ variant }), className)}
			style={{
				userSelect: 'auto',
				pointerEvents: 'auto',
				touchAction: 'auto'
			}}
			{...props}
		/>
	)
}
Toast.displayName = ToastPrimitives.Root.displayName

export const ToastAction = ({
	ref,
	className,
	...props
}: React.ComponentPropsWithRef<typeof ToastPrimitives.Action>) => (
	<ToastPrimitives.Action
		ref={ref}
		className={cn(
			'hover:bg-secondary focus:ring-ring group-[.destructive]/toast:border-muted/40 hover:group-[.destructive]/toast:border-destructive/30 hover:group-[.destructive]/toast:bg-destructive hover:group-[.destructive]/toast:text-destructive-foreground focus:group-[.destructive]/toast:ring-destructive inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors focus:ring-1 focus:outline-hidden disabled:pointer-events-none disabled:opacity-50',
			className
		)}
		{...props}
	/>
)
ToastAction.displayName = ToastPrimitives.Action.displayName

export const ToastClose = ({
	ref,
	className,
	...props
}: React.ComponentPropsWithRef<typeof ToastPrimitives.Close>) => (
	<ToastPrimitives.Close
		ref={ref}
		className={cn(
			'text-foreground hover:text-foreground focus:ring-ring absolute top-4 right-5 rounded-full transition-opacity group-hover/toast:opacity-100 group-[.destructive]/toast:text-red-300 hover:group-[.destructive]/toast:text-red-50 focus:opacity-100 focus:ring-1 focus:outline-hidden focus:group-[.destructive]/toast:ring-red-400 focus:group-[.destructive]/toast:ring-offset-red-600 md:opacity-0',
			className
		)}
		toast-close=""
		{...props}
	>
		<CloseIcon />
	</ToastPrimitives.Close>
)
ToastClose.displayName = ToastPrimitives.Close.displayName

export const ToastTitle = ({
	ref,
	className,
	...props
}: React.ComponentPropsWithRef<typeof ToastPrimitives.Title>) => (
	<ToastPrimitives.Title
		ref={ref}
		className={cn('text-base font-semibold', className)}
		{...props}
	/>
)
ToastTitle.displayName = ToastPrimitives.Title.displayName

export const ToastDescription = ({
	ref,
	className,
	...props
}: React.ComponentPropsWithRef<typeof ToastPrimitives.Description>) => (
	<ToastPrimitives.Description
		ref={ref}
		className={cn('text-base', className)}
		{...props}
	/>
)
ToastDescription.displayName = ToastPrimitives.Description.displayName

export type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>

export type ToastActionElement = React.ReactElement<typeof ToastAction>
